// @file Surface ai chat store tool calls
import { trackEvent } from '@@/bits/analytics'
import { captureException } from '@@/bits/error_tracker'
import { ShareSubpanel } from '@@/pinia/surface_share_panel'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { defineStore } from 'pinia'
import { ref } from 'vue'

enum Tool {
  TranslatePadlet = 'translate_padlet',
  OpenPanel = 'open_panel',
}

enum OpenPanelPanel {
  ExportPdf = 'pdf_export',
  ExportImage = 'image_export',
}

type ToolParams = {
  [Tool.TranslatePadlet]: { targetLanguage: string }
  [Tool.OpenPanel]: { panel: OpenPanelPanel }
}

const useSurfaceAIChatToolsStore = defineStore('surfaceAIChatToolsStore', () => {
  const surfaceTranslationsStore = useSurfaceTranslationsStore()

  const isRunningTool = ref<boolean>(false)

  const translatePadlet = async (args: ToolParams[Tool.TranslatePadlet]): Promise<void> => {
    if (args == null || args.targetLanguage == null || args.targetLanguage?.length < 1) {
      throw new Error(`SurfaceAiChatToolsStore: Invalid params for translation tool`)
    }

    await surfaceTranslationsStore.translatePadletFromSurfaceAiChat(args.targetLanguage)
  }

  const openPanel = async (args: ToolParams[Tool.OpenPanel]): Promise<void> => {
    if (args == null || args.panel == null || args.panel?.length < 1) {
      throw new Error(`SurfaceAiChatToolsStore: Invalid params for open panel tool`)
    }

    if (args.panel === OpenPanelPanel.ExportPdf) {
      const [{ useSurfacePanelsStore }, { useSurfaceSharePanelStore }] = await Promise.all([
        import('@@/pinia/surface_panels'),
        import('@@/pinia/surface_share_panel'),
      ])
      const surfacePanelsStore = useSurfacePanelsStore()
      const surfaceSharePanelStore = useSurfaceSharePanelStore()
      surfacePanelsStore.xSurfaceSharePanel = true
      surfaceSharePanelStore.activePanel = ShareSubpanel.PdfExport
    }

    if (args.panel === OpenPanelPanel.ExportImage) {
      const [{ useSurfacePanelsStore }, { useSurfaceSharePanelStore }] = await Promise.all([
        import('@@/pinia/surface_panels'),
        import('@@/pinia/surface_share_panel'),
      ])
      const surfacePanelsStore = useSurfacePanelsStore()
      const surfaceSharePanelStore = useSurfaceSharePanelStore()
      surfacePanelsStore.xSurfaceSharePanel = true
      surfaceSharePanelStore.activePanel = ShareSubpanel.ImageExport
    }
  }

  const TOOL_HANDLERS = {
    [Tool.TranslatePadlet]: translatePadlet,
    [Tool.OpenPanel]: openPanel,
  }

  const executeTool = async (params: any): Promise<void> => {
    try {
      isRunningTool.value = true
      const toolHandler = TOOL_HANDLERS[params.name]
      if (toolHandler == null) throw new Error(`SurfaceAiChatToolsStore: Unknown tool call: ${params.name}`)

      await toolHandler(params.args)

      trackEvent('Surface AI chat', 'Executed tool', null, null, params)
    } catch (e) {
      captureException(e)
    } finally {
      isRunningTool.value = false
    }
  }

  return {
    isRunningTool,
    executeTool,
  }
})

export { useSurfaceAIChatToolsStore }
